import type { DeepResearchCitationCheck, DeepResearchSource } from './types';

const CITATION_PATTERN = /\[([A-Z]\d+)\]/g;
const HEADING_PATTERN = /^##+\s+/;

const extractCitationIds = (content: string) => {
  return Array.from(content.matchAll(CITATION_PATTERN)).map((match) => match[1]);
};

const splitIntoSections = (report: string) => {
  const lines = report.split('\n');
  const sections: Array<{ heading: string; content: string }> = [];
  let currentHeading = 'Introduction';
  let currentContent: string[] = [];

  lines.forEach((line) => {
    if (HEADING_PATTERN.test(line.trim())) {
      if (currentContent.join('').trim()) {
        sections.push({ heading: currentHeading, content: currentContent.join('\n').trim() });
      }
      currentHeading = line.replace(/^##+\s*/, '').trim();
      currentContent = [];
      return;
    }

    currentContent.push(line);
  });

  if (currentContent.join('').trim()) {
    sections.push({ heading: currentHeading, content: currentContent.join('\n').trim() });
  }

  return sections;
};

export function validateReportCitations(report: string, sources: DeepResearchSource[]): DeepResearchCitationCheck {
  const sourceIds = new Set(sources.map((source) => source.id));
  const sections = splitIntoSections(report).filter((section) => {
    const heading = section.heading.toLowerCase();
    return !heading.includes('sources') && !heading.includes('methodology') && !heading.includes('limitations');
  });

  const unknownCitations = new Set<string>();
  const duplicateCitations = new Set<string>();
  const missingSections: string[] = [];

  sections.forEach((section) => {
    const citations = extractCitationIds(section.content);
    if (section.content.trim() && citations.length === 0) {
      missingSections.push(section.heading);
    }

    const seenInSection = new Set<string>();
    citations.forEach((citation) => {
      if (!sourceIds.has(citation)) {
        unknownCitations.add(citation);
      }
      if (seenInSection.has(citation)) {
        duplicateCitations.add(`${section.heading}:${citation}`);
      }
      seenInSection.add(citation);
    });
  });

  const citedSectionCount = sections.length - missingSections.length;
  const coverageScore = sections.length === 0 ? 0 : Math.round((citedSectionCount / sections.length) * 100);
  const status = unknownCitations.size > 0 || missingSections.length > 1
    ? 'failed'
    : missingSections.length === 1 || duplicateCitations.size > 0
      ? 'partial'
      : 'passed';

  return {
    status,
    coverageScore,
    sectionCount: sections.length,
    citedSectionCount,
    missingSections,
    unknownCitations: Array.from(unknownCitations),
    duplicateCitations: Array.from(duplicateCitations),
  };
}
